import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import { fetchMerchandisingQueryGroups } from 'features/merchandising/state/merchandisingSlice';

const normalizeQuery = (query) => String(query || '').trim().toLocaleLowerCase();

export function useQueryRuleIndex() {
  const dispatch = useDispatch();
  const { capabilities = {} } = useShellAppContext();
  const groups = useSelector((state) => state.merchandising?.queryGroups || {});
  const enabled = Boolean(capabilities.merchandising_query_groups_v1);

  useEffect(() => {
    if (!enabled || groups.request?.status !== 'idle') return;
    dispatch(fetchMerchandisingQueryGroups({ limit: 100 }));
  }, [dispatch, enabled, groups.request?.status]);

  useEffect(() => {
    if (!enabled || groups.request?.status !== 'success' || !groups.nextCursor) return;
    dispatch(fetchMerchandisingQueryGroups({ cursor: groups.nextCursor, limit: 100 }));
  }, [dispatch, enabled, groups.nextCursor, groups.request?.status]);

  return useMemo(() => {
    const index = new Map();
    if (!enabled) return index;
    (groups.order || []).forEach((id) => {
      const group = groups.entities?.[id];
      if (!group) return;
      (group.members || []).forEach((member) => {
        const key = normalizeQuery(member.query);
        if (!key) return;
        const current = index.get(key) || { active: [], other: [] };
        if (group.status === 'active') current.active.push(group);
        else if (group.status !== 'archived') current.other.push(group);
        index.set(key, current);
      });
    });
    return index;
  }, [enabled, groups.entities, groups.order]);
}

export const rulesForQuery = (index, query) => (
  index.get(normalizeQuery(query)) || { active: [], other: [] }
);
